/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Alert } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { TextInput } from 'src/components/ts-components/TextInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import Text from 'src/components/ts-components/Text'
import CopyComponent from 'src/components/CopyComponent'
import { useCountries } from '@sportninja/common/hooks/useCountries'
import { useTaxContext } from './TaxContext'
import CurrencyInputField from 'react-currency-input-field'
import { getLocaleConfig } from '@sportninja/common/currency'
import { ErrorLabel } from 'src/components/ts-components/ErrorLabel'
import { paragraphMedium, weightRegular } from 'src/components/css'

const fieldWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TaxModalContent: React.FC = () => {
  const { state, dispatch, isEdit } = useTaxContext()

  const { countries, refetch: fetchCountries } = useCountries()

  useEffect(() => {
    fetchCountries()
  }, [fetchCountries])

  const currencyCountryOptions = useMemo(() => {
    return countries
      .filter((c) => c.currency_code)
      .map((c) => ({
        label: c.name,
        value: String(c.id),
      }))
  }, [countries])

  const selectedCountry = useMemo(() => {
    if (!state.currencyCountryId) {
      return undefined
    }
    return countries.find((c) => String(c.id) === state.currencyCountryId)
  }, [countries, state.currencyCountryId])

  const localeConfig = useMemo(
    () => getLocaleConfig(selectedCountry),
    [selectedCountry]
  )

  // Internal state for the tax rate input — always synced from state.taxRate
  const [internalTaxRate, setInternalTaxRate] = useState(state.taxRate || '')

  // Sync internal value when state.taxRate or locale changes
  useEffect(() => {
    const val = state.taxRate || ''
    // Convert '.' decimal to locale-specific separator for display
    const localized = val.replace('.', localeConfig.decimalSeparator)
    setInternalTaxRate(localized)
  }, [state.taxRate, localeConfig.decimalSeparator])

  const handleFieldChange = useCallback(
    (field: string) => (value: string) => {
      dispatch({
        type: 'SET_FIELD',
        field: field as keyof typeof state,
        value,
      })
    },
    [dispatch]
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px;
        height: 100%;
        overflow-y: auto;
        width: 100%;
      `}
    >
      {/* Header with ID */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Tax Info
        </Text>
        {isEdit && state.id ? (
          <div>
            <CopyComponent id={state.id} />
          </div>
        ) : null}
      </div>

      {/* Tax Name */}
      <div css={fieldWrapperStyle}>
        <TextInput
          name='name'
          label='Name'
          value={state.taxName}
          setValue={handleFieldChange('taxName')}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          placeholder='Tax Name'
          required
        />
      </div>

      {/* Tax Description */}
      <div css={fieldWrapperStyle}>
        <TextInput
          name='description'
          label='Description'
          value={state.taxDescription}
          setValue={handleFieldChange('taxDescription')}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          placeholder='Tax Description'
        />
      </div>

      {/* Country */}
      <div css={fieldWrapperStyle}>
        <SelectInput
          label='Country *'
          allowClear={false}
          showSearch={true}
          filterOption={(input, option) =>
            (option?.label ?? '')
              .toString()
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          options={currencyCountryOptions}
          value={state.currencyCountryId}
          onChange={(value) => {
            dispatch({
              type: 'SET_FIELD',
              field: 'currencyCountryId',
              value,
            })
          }}
          disabled={false}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          name='currency_country_id'
        />
      </div>

      {/* Tax Rate */}
      <div css={fieldWrapperStyle}>
        <Text
          variant='paragraphSmall'
          color={colors.WHITE}
          weight='regular'
          style={`
            align-self: stretch;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          Tax Rate (%) *
        </Text>
        <CurrencyInputField
          id='tax_rate'
          name='tax_rate'
          prefix=''
          suffix='%'
          placeholder={`0${localeConfig.decimalSeparator}00%`}
          value={internalTaxRate}
          decimalsLimit={4}
          decimalSeparator={localeConfig.decimalSeparator}
          groupSeparator={localeConfig.groupSeparator}
          allowDecimals={true}
          allowNegativeValue={false}
          onValueChange={(v) => {
            setInternalTaxRate(v || '')
          }}
          onBlur={() => {
            // Normalize locale decimal separator to '.' for the context state
            const normalized = internalTaxRate.replace(
              localeConfig.decimalSeparator,
              '.'
            )
            dispatch({
              type: 'SET_FIELD',
              field: 'taxRate',
              value: normalized,
            })
          }}
          css={css`
            height: 40px !important;
            border-radius: 8px;
            border: 1px solid ${colors.SECONDARY_100};
            background: ${colors.SECONDARY_200};
            width: 100%;
            padding: 6px 8px !important;
            ${paragraphMedium}
            ${weightRegular}
            ${state.fieldErrors?.includes('tax_rate')
              ? `border-color: ${colors.ERROR};`
              : ''}
            color: ${colors.WHITE};
            &:focus {
              outline: none;
              border-color: ${colors.PRIMARY_100};
              box-shadow: 0 0 0 1px ${colors.PRIMARY_100};
            }
            &::placeholder {
              color: ${colors.NEUTRAL_100};
            }
            transition: border-color 0.15s ease-in-out,
              box-shadow 0.15s ease-in-out;
          `}
        />
        <ErrorLabel
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          name='tax_rate'
        />
      </div>

      {/* Error Display */}
      {state.error && (
        <Alert
          message={state.error}
          type='error'
          showIcon
          css={css`
            background: ${colors.ERROR_50};
            border: 1px solid ${colors.ERROR_300};
            border-radius: 8px;
            margin-top: 8px;
            .ant-alert-message {
              color: ${colors.ERROR_300};
            }
            .ant-alert-icon {
              color: ${colors.ERROR_300};
            }
          `}
        />
      )}
    </div>
  )
}

export default TaxModalContent
